import React, { useState, useEffect } from "react";

// React DateTime
import moment from "moment";

// Components
import Breadcrumbs from "../../../components/Breadcrumbs";
import ViewSoModal from "../../../components/Modal/ViewSoModal";
import PrintInvoiceModal from "../../../components/Modal/PrintInvoiceModal";
import SearchAndFilter from "../../../components/Search/SearchAndFilter";
import Pagination from "../../../components/Footer/Pagination";

import { formatDate } from "../../../../utils/DateUtils";
import {
  getData,
  formatCurrency,
  parseCurrency,
} from "../../../../utils/SamuiUtils";
import { getReport } from "../../../../utils/ReportUtils";
import {
  useDataTableKeyboardNavigation,
  getDataTableRowStyle,
  getDataTableKeyboardInstructions
} from '../../../../utils/DataTableKeyboardNavigation';

const Main = ({ name, dataMasterList, onPageInsert, onPageUpdate }) => {
  const [filteredDetailList, setFilteredDetailList] = useState([]);
  const [docNo, setDocNo] = useState("");
  const [arName, setArName] = useState("");
  const [soTotal, setSoTotal] = useState(0);
  const [soTotalVat, setSoTotalVat] = useState(0);
  const [soNetTotal, setSoNetTotal] = useState(0);
  const [receiptDiscount, setReceiptDiscount] = useState(0);
  const [discountValue, setDiscountValue] = useState(0);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(
    localStorage.getItem('currentPage') ? parseInt(localStorage.getItem('currentPage')) : 1
  );
  const [itemsPerPage, setItemsPerPage] = useState(
    localStorage.getItem('itemsPerPageDropdown') ? parseInt(localStorage.getItem('itemsPerPageDropdown')) : 10
  );

  // Search and filter states
  const [searchTerm, setSearchTerm] = useState(
    localStorage.getItem('searchTerm') || ''
  );
  const [filteredItems, setFilteredItems] = useState([]);

  // Date filter states
  const [startDate, setStartDate] = useState(() => {
    const storedStartDate = localStorage.getItem('startDate');
    return storedStartDate || moment().startOf('month').format('YYYY-MM-DD');
  });

  const [endDate, setEndDate] = useState(() => {
    const storedEndDate = localStorage.getItem('endDate');
    return storedEndDate || moment().endOf('month').format('YYYY-MM-DD');
  });

  // DataTable keyboard navigation state
  const [selectedRowIndex, setSelectedRowIndex] = useState(-1);

  // Initialize data
  useEffect(() => {
    if (dataMasterList && dataMasterList.length > 0) {
      setCurrentPage(1);
      filterItems();
    } else {
      setFilteredItems([]);
    }
  }, [dataMasterList]);

  // Filter when search term or dates change
  useEffect(() => {
    setCurrentPage(1);
    filterItems();
    localStorage.setItem('searchTerm', searchTerm);
    localStorage.setItem('startDate', startDate);
    localStorage.setItem('endDate', endDate);
  }, [searchTerm, startDate, endDate, dataMasterList]);

  // Filter items function
  const filterItems = () => {
    try {
      let filtered = [...dataMasterList];

      // Date filtering
      const start = moment(startDate, 'YYYY-MM-DD');
      const end = moment(endDate, 'YYYY-MM-DD');

      filtered = filtered.filter((item) => {
        const itemDate = moment(item.Doc_Date, 'YYYY-MM-DD');
        return itemDate.isBetween(start, end, null, '[]');
      });

      // Search filtering
      if (searchTerm.trim() !== '') {
        const lowercasedSearchTerm = searchTerm.toLowerCase();
        filtered = filtered.filter((item) =>
          (item.Doc_No && item.Doc_No.toLowerCase().includes(lowercasedSearchTerm)) ||
          (item.AR_Name && item.AR_Name.toLowerCase().includes(lowercasedSearchTerm)) ||
          (item.DocSetStaus_Name && item.DocSetStaus_Name.toLowerCase().includes(lowercasedSearchTerm))
        );
      }

      setFilteredItems(filtered);
    } catch (error) {
      console.error('Filter error:', error.message);
      setFilteredItems([]);
    }
  };

  // Pagination calculations
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    setSelectedRowIndex(-1);
    localStorage.setItem('currentPage', pageNumber);
  };

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredItems.slice(indexOfFirstItem, indexOfLastItem);
  const discountAmount = (soNetTotal || 0) * ((receiptDiscount || 0) / 100);
  const grandTotal = (soNetTotal || 0) - (discountAmount || 0);

  const onRowSelected = async (fromData, index = -1) => {
    setSelectedRowIndex(index);
    const dataDetailList = await getData(
      "API_1302_BL_D",
      `Doc_Id = ${parseCurrency(fromData.Doc_Id)} ORDER BY SO_Date, SO_No ASC`
    );

    let total = 0;
    let totalVat = 0;

    if (dataDetailList && dataDetailList.length > 0) {
      setFilteredDetailList(dataDetailList);

      for (const fromDetail of dataDetailList) {
        total += fromDetail.SO_NetTotal - fromDetail.Total_Vat;
        totalVat += fromDetail.Total_Vat;
      }
    } else {
      setFilteredDetailList([]);
    }

    setDocNo(fromData.Doc_No);
    setArName(fromData.AR_Name);
    setSoTotal(total);
    setSoTotalVat(totalVat);
    setSoNetTotal(total + totalVat);
    setDiscountValue(fromData.Discount_Value);
    setReceiptDiscount(fromData.Discount_Value)
  };

  // Field mappings for keyboard navigation
  const fieldMappings = [
    { field: 'Doc_No', label: 'เลขที่เอกสาร' },
    { field: 'Doc_Date', label: 'วันที่' },
    { field: 'AR_Name', label: 'ลูกค้า' }
  ];

  // Keyboard navigation
  useDataTableKeyboardNavigation(
    true, // isActive
    currentItems,
    selectedRowIndex,
    setSelectedRowIndex,
    onRowSelected,
    fieldMappings,
    currentPage,
    handlePageChange,
    filteredItems.length,
    itemsPerPage
  );

  // เปิด Modal สำหรับดูรายละเอียดใบขาย
  const [showViewSoModal, setShowViewSoModal] = useState(false);
  const [selectedSoData, setSelectedSoData] = useState(null);
  const handleViewSoClose = () => {
    setShowViewSoModal(false);
    setSelectedSoData(null);

    // ลบ class 'modal-open' จาก body
    document.body.classList.remove("modal-open");

    // ลบ element ที่อาจถูกซ้อน เช่น backdrop ของ Modal
    const backdrops = document.getElementsByClassName("modal-backdrop");
    while (backdrops.length > 0) {
      backdrops[0].parentNode.removeChild(backdrops[0]);
    }
  };
  const handleViewSo = (soData) => {
    setSelectedSoData(soData);
    setShowViewSoModal(true);
  };

  const [showOptionInvModal, setShowOptionInvModal] = useState(false);
  const [selectBiNo, setSelectBiNo] = useState(null);
  const [selectInvNo, setSelectInvNo] = useState(null);

  return (
    <>
      <Breadcrumbs
        page={name}
        items={[
          { name: "งานบัญชี", url: "/account" },
          { name: name, url: "/invoice" },
        ]}
      />
      <div
        className="container-fluid card py-3 hidden-scrollbar"
        style={{ maxHeight: "auto", overflowY: "auto" }}
      >
        {/* Search and Filter Section */}
        <SearchAndFilter
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          searchPlaceholder="ค้นหาเลขเอกสาร, ลูกค้า, สถานะเอกสาร..."
          startDate={startDate}
          endDate={endDate}
          setStartDate={setStartDate}
          setEndDate={setEndDate}
          showDateFilter={true}
          onAddNew={onPageInsert}
          addButtonText={`สร้าง${name}`}
          showAddButton={true}
        />
        <hr />
        <div className="row">
          <div
            className="col-7 mb-1"
            style={{ borderRight: "2px solid #c7c8c9" }}
          >
            <div
              className="table-responsive"
              style={{ maxHeight: "650px", overflowY: "auto" }}
            >
              <table
                id="basic-datatables"
                className="table table-striped table-hover"
              >
                <thead className="thead-dark">
                  <tr>
                    <th className="text-center" style={{ width: "10%" }}>
                      วันที่
                    </th>
                    <th className="text-center" style={{ width: "13%" }}>
                      สถานะเอกสาร
                    </th>
                    <th className="text-center" style={{ width: "12%" }}>
                      สถานะจ่าย
                    </th>
                    <th className="text-center" style={{ width: "13%" }}>
                      ประเภทการจ่าย
                    </th>
                    <th className="text-center" style={{ width: "10%" }}>
                      เลขใบวางบิล
                    </th>
                    <th className="text-center" style={{ width: "20%" }}>
                      เลขใบเสร็จรับเงิน
                    </th>
                    <th className="text-center" style={{ width: "15%" }}>
                      ชื่อลูกค้า
                    </th>
                    <th className="text-center" style={{ width: "6%" }}>
                      รวมราคา
                    </th>
                    <th className="text-center" style={{ width: "1%" }}>
                      <i className="fa fa-edit" />
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredItems.length > 0 ? (
                    currentItems.map((data, index) => {
                      const statusColorMap = {
                        "รออนุมัติ": "#EAA300",
                        "อนุมัติ": "#13A10E",
                        "ปิดงาน": "#808080",
                        "ยกเลิก": "#000000",
                      };
                      return (
                        <tr
                          key={data.Doc_Id || index + 1}
                          onClick={() => onRowSelected(data, index)}
                          style={getDataTableRowStyle(
                            index,
                            selectedRowIndex,
                            docNo,
                            data,
                            'Doc_No'
                          )}
                        >
                          <td className="text-center">
                            {formatDate(data.Doc_Date)}
                          </td>
                          <td className="text-center">
                            <button
                              className="btn"
                              style={{
                                backgroundColor: statusColorMap[data.DocSetStaus_Name] || "gray",
                                color: "white",
                                width: "100%",
                              }}
                            >
                              {data.DocSetStaus_Name}
                            </button>
                          </td>
                          <td className="text-center">
                            {data.Doc_Status !== 13 ? (
                              <button
                                className="btn"
                                style={{
                                  backgroundColor: data.DocBLStatusPaid_Colour,
                                  color: "white",
                                  width: "100%",
                                }}
                              >
                                {data.DocBLStatusPaid_Name}
                              </button>
                            ) : (
                              <button
                                className="btn"
                                style={{
                                  backgroundColor: "black",
                                  color: "white",
                                  width: "100%",
                                }}
                              >
                                ยกเลิก
                              </button>
                            )}
                          </td>
                          <td className="text-center">
                            {data.DocBLStatusPaid_Name !== "จ่ายครบ" ? (
                              <span
                              // className="btn"
                              // style={{
                              //   backgroundColor: data.DocBLStatusPaid_Colour,
                              //   color: "white",
                              //   width: "100%",
                              // }}
                              >
                                -
                              </span>
                            ) : (
                              <span
                              // className="btn"
                              // style={{
                              //   backgroundColor: "black",
                              //   color: "white",
                              //   width: "100%",
                              // }}
                              >
                                {data.DocStatusPaid_Type_Name}
                              </span>
                            )}
                          </td>
                          <td className="text-center">{data.Doc_No}</td>
                          <td className="text-left">
                            {data.Doc_INV_No || "-"}
                            {data.Doc_INV_No && (
                              <>
                                <button
                                  className="btn btn-link btn-sm px-3"
                                  onClick={() => {
                                    setSelectBiNo(data.Doc_No);
                                    setSelectInvNo(data.Doc_INV_No);
                                    setShowOptionInvModal(true);
                                  }}
                                >
                                  <i
                                    className="fa fa-print"
                                    style={{ fontSize: "16px" }}
                                  ></i>
                                </button>
                                {showOptionInvModal && (
                                  <PrintInvoiceModal
                                    showOptionInvModal={showOptionInvModal}
                                    setShowOptionInvModal={setShowOptionInvModal}
                                    onHandleInvoice1={() =>
                                      getReport("inv1", selectInvNo)
                                    }
                                    onHandleInvoice2={() =>
                                      getReport("inv2", selectInvNo)
                                    }
                                    onHandleInvoice3={() =>
                                      getReport(localStorage.getItem("company") === "1"
                                        ? "inv_water_receipt" : "inv_ice_receipt", selectBiNo)
                                    }
                                    invNo={selectInvNo}
                                  />
                                )}
                              </>
                            )}
                          </td>
                          <td className="text-left">
                            {data.AR_Name.length > 18
                              ? `${data.AR_Name.slice(0, 16)}...`
                              : data.AR_Name}
                          </td>
                          <td className="text-end">
                            {formatCurrency(data.BL_Net_Total)}
                          </td>
                          <td className="text-center">
                            <button
                              className="btn btn-sm"
                              onClick={() => onPageUpdate(data.Doc_No)}
                            >
                              <i className="fa fa-edit" />
                            </button>
                          </td>
                        </tr>
                      )
                    })
                  ) : (
                    <tr>
                      <td colSpan="9">
                        <center>
                          <h5>ไม่พบข้อมูล</h5>
                        </center>
                      </td>
                    </tr>
                  )}
                </tbody>
                {filteredItems.length > 0 && (
                  <tfoot>
                    <tr>
                      <td
                        colSpan="8"
                        className="text-end"
                        style={{
                          fontSize: "16px",
                          fontWeight: "bold",
                          color: "#333",
                        }}
                      >
                        <div className="row">
                          {/* รวม */}
                          <div className="col-12">
                            <span>รวมยอด วางบิล</span>
                            <span
                              style={{ paddingLeft: "10px", color: "#EF6C00" }}
                            >
                              {formatCurrency(
                                filteredItems.reduce(
                                  (total, item) => total + item.BL_Net_Total,
                                  0
                                )
                              )}
                            </span>
                            <span style={{ paddingLeft: "10px" }}>บาท</span>
                          </div>
                        </div>
                      </td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>

            {/* Pagination & Items per page selector */}
            <div className="bg-white mt-2" style={{ padding: '8px 0' }}>
              <div className="row m-0">
                <div className="col-12 d-flex justify-content-between align-items-center flex-wrap py-2">
                  <div className="d-flex align-items-center flex-wrap">
                    <span className="me-2">แสดง:</span>
                    <select
                      className="form-select me-2"
                      style={{ width: 'auto', minWidth: '70px' }}
                      value={itemsPerPage}
                      onChange={(e) => {
                        const newItemsPerPage = parseInt(e.target.value);
                        setItemsPerPage(newItemsPerPage);
                        localStorage.setItem('itemsPerPageDropdown', newItemsPerPage);
                        setCurrentPage(1);
                      }}
                    >
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                    <span>รายการต่อหน้า | แสดง {indexOfFirstItem + 1} ถึง {Math.min(indexOfLastItem, filteredItems.length)} จาก {filteredItems.length} รายการ</span>
                  </div>
                  <div className="d-flex align-items-center flex-wrap">
                    <Pagination
                      currentPage={currentPage}
                      setCurrentPage={setCurrentPage}
                      itemsPerPage={itemsPerPage}
                      dataMasterList={filteredItems}
                      handlePageChange={handlePageChange}
                    />
                  </div>

                </div>
              </div>
              <div className="text-muted" style={{ fontSize: "12px" }}>
                {getDataTableKeyboardInstructions(false, true)}
              </div>

            </div>
          </div>
          <div className="col-5 mb-3">
            {filteredDetailList.length !== 0 && (
              <div className="justify-content-between align-items-center mb-3">
                <div className="row">
                  <div className="col-12">
                    <p className="card-title mb-0">
                      เอกสารเลขที่: {docNo} {arName}
                    </p>
                  </div>
                  <div className="col-12 d-flex justify-content-end">
                    {localStorage.getItem("company") === "1" ? (
                      <>
                        <button
                          className="btn d-flex align-items-center text-white me-2"
                          style={{
                            backgroundColor: "rgb(239, 108, 0)",
                            fontSize: "16px",
                          }}
                          onClick={() => getReport("bi1", docNo)}
                        >
                          <i
                            className="fa fa-print me-2"
                            aria-hidden="true"
                          ></i>
                          ใบวางบิล
                        </button>
                        {/* <button
                        className="btn d-flex align-items-center text-white"
                        style={{
                          backgroundColor: 'rgb(239, 108, 0)',
                          fontSize: '16px',
                        }}
                        onClick={() => getReport('bi2', docNo)}
                      >
                        <i className="fa fa-print me-2" aria-hidden="true"></i>
                        พิมพ์ใบวางบิล (สำเนา)
                      </button> */}
                      </>
                    ) : (
                      <>
                        <button
                          className="btn d-flex align-items-center text-white me-2"
                          style={{
                            backgroundColor: "rgb(239, 108, 0)",
                            fontSize: "16px",
                          }}
                          onClick={() => getReport("bi1", docNo)}
                        >
                          <i
                            className="fa fa-print me-2"
                            aria-hidden="true"
                          ></i>
                          พิมพ์ใบวางบิล (ฟอร์ม A4)
                        </button>
                        <button
                          className="btn d-flex align-items-center text-white"
                          style={{
                            backgroundColor: "rgb(239, 108, 0)",
                            fontSize: "16px",
                          }}
                          onClick={() => getReport("bl_repeat", docNo)}
                        >
                          <i
                            className="fa fa-print me-2"
                            aria-hidden="true"
                          ></i>
                          พิมพ์ใบวางบิล (ฟอร์มกระดาษต่อเนื่อง)
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}
            <div
              className="table-responsive"
              style={{
                maxHeight: "600px",
                overflowY: "auto",
              }}
            >
              <table
                id="basic-datatables"
                className="table table-striped table-hover"
                style={{ minWidth: "100%" }}
              >
                <thead className="thead-dark">
                  <tr>
                    <th className="text-center" style={{ width: "2%" }}>
                      ลำดับ
                    </th>
                    <th className="text-center" style={{ width: "9%" }}>
                      ประเภท
                    </th>
                    <th className="text-center" style={{ width: "18%" }}>
                      วันที่เอกสาร
                    </th>
                    <th className="text-center" style={{ width: "18%" }}>
                      เลขที่ใบขาย
                    </th>
                    <th className="text-center" style={{ width: "18%" }}>
                      รวมราคา
                    </th>
                    <th className="text-center" style={{ width: "14%" }}>
                      ราคา VAT
                    </th>
                    <th className="text-center" style={{ width: "30%" }}>
                      รวมสุทธิ
                    </th>
                    <th className="text-center" style={{ width: "2%" }}>
                      View
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredDetailList.length > 0 ? (
                    filteredDetailList.map((item, index) => (
                      <tr key={item.DT_Id || index + 1}>
                        <td className="text-center">{index + 1}</td>
                        <td className="text-center">
                          <button
                            className="btn"
                            style={{
                              backgroundColor:
                                item.Doc_Is_PickingList === 0 ||
                                  item.Doc_Is_PickingList === null
                                  ? "#EAA300"
                                  : "#13A10E",
                              color: "white",
                              width: "100%",
                            }}
                          >
                            {item.Doc_Is_PickingList === 0 ||
                              item.Doc_Is_PickingList === null
                              ? "S"
                              : "V"}
                          </button>
                        </td>
                        <td className="text-center">
                          {formatDate(item.SO_Date)}
                        </td>
                        <td className="text-center">{item.SO_No}</td>
                        <td className="text-end">
                          {formatCurrency(item.SO_NetTotal - item.Total_Vat)}
                        </td>
                        <td className="text-end">
                          {formatCurrency(item.Total_Vat)}
                        </td>
                        <td className="text-end">
                          {formatCurrency(item.SO_NetTotal )}
                        </td>
                        <td className="text-center">
                          <i
                            className="fa fa-eye"
                            style={{ color: "#EF6C00", cursor: "pointer" }}
                            onClick={() => handleViewSo(item)}
                          ></i>
                        </td>

                        {showViewSoModal && selectedSoData && (
                          <ViewSoModal
                            showViewSoModal={showViewSoModal}
                            handleViewSoClose={handleViewSoClose} // ใช้ฟังก์ชันปิด Modal
                            mode={"U"}
                            name={`ใบขายสินค้าเลขที่ ${selectedSoData.SO_No}`}
                            maxDocNo={selectedSoData.SO_No} // ส่งหมายเลขใบขาย
                          />
                        )}
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="8">
                        <center>
                          <h5>ไม่พบข้อมูล</h5>
                        </center>
                      </td>
                    </tr>
                  )}
                </tbody>
                {filteredDetailList.length > 0 && (
                  <tfoot>
                    <tr>
                      <td
                        colSpan="4"
                        className="text-end"
                        style={{
                          fontSize: "16px",
                          fontWeight: "bold",
                          color: "#333",
                        }}
                      >
                        <div className="text-end">
                          <span>รวมราคา</span>
                        </div>
                      </td>
                      <td
                        colSpan="1"
                        className="text-end"
                        style={{
                          fontSize: "16px",
                          fontWeight: "bold",
                          color: "#333",
                        }}
                      >
                        {/* รวมราคา */}
                        <span className="text-end" style={{ color: "#EF6C00" }}>
                          {formatCurrency(soTotal)}
                        </span>
                      </td>
                      <td
                        colSpan="1"
                        className="text-end"
                        style={{
                          fontSize: "16px",
                          fontWeight: "bold",
                          color: "#333",
                        }}
                      >
                        {/* VAT */}
                        <span style={{ color: "#EF6C00" }}>
                          {formatCurrency(soTotalVat)}
                        </span>
                      </td>
                      <td
                        colSpan="1"
                        className="text-end"
                        style={{
                          fontSize: "16px",
                          fontWeight: "bold",
                          color: "#333",
                        }}
                      >
                        {/* รวมราคาสุทธิ */}
                        <span style={{ color: "#EF6C00" }}>
                          {formatCurrency(soNetTotal)}
                        </span>
                      </td>
                      <td colSpan="1"></td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
            <div className="mt-2">
              <div className="d-flex justify-content-end align-items-center mt-1">
                <label className="me-2">ราคารวมสุทธิ</label>
                <input
                  type="text"
                  className="form-control text-end input-spacing"
                  style={{ width: '100px' }}
                  value={formatCurrency(soNetTotal || 0)}
                  disabled={true}
                />
              </div>
              <div className="d-flex justify-content-end align-items-center mt-1">
                <label className="me-2">รวมส่วนลด
                  {(discountValue || discountValue !== 0) && (
                    " " + discountValue + "%")
                  }
                </label>
                <input
                  type="text"
                  className="form-control text-end input-spacing"
                  style={{ width: '100px' }}
                  value={formatCurrency(discountAmount || 0)}
                  disabled={true}
                />
              </div>
              <hr />
              <div className="d-flex justify-content-end align-items-center mt-1">
                <label className="me-2"><h5 className="m-0">ราคารวมสุทธิ</h5></label>
                <input
                  type="text"
                  className="form-control text-end input-spacing"
                  style={{ width: '100px', color: 'red', fontWeight: 'bold', fontSize: '18px' }}
                  value={formatCurrency(grandTotal || 0)}
                  disabled={true}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Main;
